import React, { useState, useRef } from 'react';
import './SearchDebug.css';

function SearchDebug({ apiUrl }) {
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const currentQueryRef = useRef(''); // Track the current query being processed
  const [loading, setLoading] = useState({
    vector: false,
    topicEntity: false,
    combined: false,
    llmFiltered: false
  });
  const [results, setResults] = useState({
    vector: null,
    topicEntity: null,
    combined: null,
    llmFiltered: null
  });
  const [errors, setErrors] = useState({
    vector: null,
    topicEntity: null,
    combined: null,
    llmFiltered: null
  });
  const [timings, setTimings] = useState({
    vector: null,
    topicEntity: null,
    combined: null,
    llmFiltered: null
  });
  const [totalQuestions, setTotalQuestions] = useState(null);
  const [queryAnalysis, setQueryAnalysis] = useState({
    topics: [],
    entities: []
  });
  const [enabledMethods, setEnabledMethods] = useState({
    vector: true,
    topicEntity: true,
    combined: true,
    llmFiltered: true
  });

  const placeholderQuestions = [
    "Why should I care about spirituality?",
    "Is consciousness generated by the brain?",
    "How does Vedanta view suffering?",
    "Does Vedanta promote indifference to social problems?",
    "What is vedantic view of evolution?"
  ];

  const fetchSearchMethod = async (method, question) => {
    const startTime = performance.now();
    const queryForThisFetch = question; // Capture the question at fetch start
    
    console.log(`🔍 Fetching ${method} search for query: "${queryForThisFetch}"`);
    
    setLoading(prev => ({ ...prev, [method]: true }));
    setErrors(prev => ({ ...prev, [method]: null }));
    
    try {
      const endpoint = method === 'bm25' ? 'bm25' : 
                      method === 'vector' ? 'vector' : 
                      method === 'combined' ? 'combined' :
                      method === 'llmFiltered' ? 'llm-filtered' :
                      'topic-entity';
      
      // Add timestamp to prevent any caching
      const timestamp = Date.now();
      const url = `${apiUrl}/api/search/${endpoint}?query=${encodeURIComponent(question)}&top_n=5&_t=${timestamp}`;
      console.log(`   URL: ${url.substring(0, 100)}...`); // Log first 100 chars to avoid huge logs
      
      const response = await fetch(
        url,
        {
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          cache: 'no-store', // Stronger than 'no-cache'
          method: 'GET'
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ API error for ${method}: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`✅ ${method} response received:`, {
        hasResults: !!data.results,
        resultsLength: data.results?.length || 0,
        dataKeys: Object.keys(data)
      });
      
      // Check if this fetch is still relevant (user hasn't clicked a new question)
      if (currentQueryRef.current !== queryForThisFetch) {
        console.log(`Ignoring stale ${method} results for query: "${queryForThisFetch}" (current: "${currentQueryRef.current}")`);
        return; // Don't update state if query has changed
      }
      
      const endTime = performance.now();
      const duration = ((endTime - startTime) / 1000).toFixed(2);
      
      // Handle topic-entity response which includes query analysis
      if (method === 'topicEntity') {
        console.log('Topic/Entity response:', data);
        if (data.query_topics !== undefined) {
          setQueryAnalysis({
            topics: data.query_topics || [],
            entities: data.query_entities || []
          });
          setResults(prev => ({ ...prev, [method]: data.results || [] }));
        } else {
          // Fallback: if response is just an array, it's the old format
          setResults(prev => ({ ...prev, [method]: Array.isArray(data) ? data : [] }));
        }
      } else if (method === 'combined' || method === 'llmFiltered') {
        // Combined/LLM-filtered search response has results array
        console.log(`${method} response:`, data);
        console.log(`${method} results array:`, data.results);
        console.log(`${method} results length:`, data.results?.length || 0);
        const resultsArray = data.results || [];
        setResults(prev => ({ ...prev, [method]: resultsArray }));
        if (data.total_questions_in_db !== undefined) {
          setTotalQuestions(data.total_questions_in_db);
        }
      } else {
        setResults(prev => ({ ...prev, [method]: data }));
      }
      
      setTimings(prev => ({ ...prev, [method]: duration }));
    } catch (err) {
      // Only set error if this fetch is still relevant
      if (currentQueryRef.current === queryForThisFetch) {
        setErrors(prev => ({ ...prev, [method]: err.message }));
        console.error(`Error fetching ${method} search:`, err);
      }
    } finally {
      // Only update loading state if this fetch is still relevant
      if (currentQueryRef.current === queryForThisFetch) {
        setLoading(prev => ({ ...prev, [method]: false }));
      }
    }
  };

  const handleQuestionClick = async (question) => {
    // Update the current query reference immediately
    currentQueryRef.current = question;
    
    setSelectedQuestion(question);
    setResults({ vector: null, topicEntity: null, combined: null, llmFiltered: null });
    setErrors({ vector: null, topicEntity: null, combined: null, llmFiltered: null });
    setTimings({ vector: null, topicEntity: null, combined: null, llmFiltered: null });
    setTotalQuestions(null);
    setQueryAnalysis({ topics: [], entities: [] });

    // Fetch only enabled methods in parallel
    const methodsToFetch = [];
    if (enabledMethods.vector) methodsToFetch.push(fetchSearchMethod('vector', question));
    if (enabledMethods.topicEntity) methodsToFetch.push(fetchSearchMethod('topicEntity', question));
    if (enabledMethods.combined) methodsToFetch.push(fetchSearchMethod('combined', question));
    if (enabledMethods.llmFiltered) methodsToFetch.push(fetchSearchMethod('llmFiltered', question));
    
    if (methodsToFetch.length > 0) {
      await Promise.all(methodsToFetch);
    }

    // Get total questions count (using dedicated endpoint that doesn't trigger searches)
    try {
      const response = await fetch(
        `${apiUrl}/api/search/count`,
        {
          headers: {
            'Accept': 'application/json',
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setTotalQuestions(data.total_questions_in_db);
      }
    } catch (err) {
      // Ignore error for total count
    }
  };

  const handleCustomQuery = async (e) => {
    e.preventDefault();
    if (selectedQuestion.trim()) {
      await handleQuestionClick(selectedQuestion);
    }
  };

  return (
    <div className="search-debug">
      <div className="debug-header">
        <h2>Search Comparison Debug</h2>
        <p className="debug-description">
          Compare Vector and Topic/Entity search results. No LLM filtering applied.
        </p>
      </div>

      <div className="debug-controls">
        <div className="search-method-selector">
          <h3>Search Methods:</h3>
          <div className="method-checkboxes">
            <label className="method-checkbox">
              <input
                type="checkbox"
                checked={enabledMethods.vector}
                onChange={(e) => setEnabledMethods(prev => ({ ...prev, vector: e.target.checked }))}
                disabled={loading.vector || loading.topicEntity || loading.combined || loading.llmFiltered}
              />
              <span>Vector</span>
            </label>
            <label className="method-checkbox">
              <input
                type="checkbox"
                checked={enabledMethods.topicEntity}
                onChange={(e) => setEnabledMethods(prev => ({ ...prev, topicEntity: e.target.checked }))}
                disabled={loading.vector || loading.topicEntity || loading.combined || loading.llmFiltered}
              />
              <span>Topic/Entity</span>
            </label>
            <label className="method-checkbox">
              <input
                type="checkbox"
                checked={enabledMethods.combined}
                onChange={(e) => setEnabledMethods(prev => ({ ...prev, combined: e.target.checked }))}
                disabled={loading.vector || loading.topicEntity || loading.combined || loading.llmFiltered}
              />
              <span>Combined (Vector + Topics)</span>
            </label>
            <label className="method-checkbox">
              <input
                type="checkbox"
                checked={enabledMethods.llmFiltered}
                onChange={(e) => setEnabledMethods(prev => ({ ...prev, llmFiltered: e.target.checked }))}
                disabled={loading.vector || loading.topicEntity || loading.combined || loading.llmFiltered}
              />
              <span>LLM Filtered (Homepage)</span>
            </label>
          </div>
        </div>

        <div className="pre-canned-questions">
          <h3>Pre-canned Questions:</h3>
          <div className="question-buttons">
            {placeholderQuestions.map((pq, index) => (
              <button
                key={index}
                type="button"
                className={`question-button ${selectedQuestion === pq ? 'active' : ''}`}
                onClick={() => handleQuestionClick(pq)}
                disabled={loading.vector || loading.topicEntity || loading.combined || loading.llmFiltered}
              >
                {pq}
              </button>
            ))}
          </div>
        </div>

        <form className="custom-query-form" onSubmit={handleCustomQuery}>
          <label htmlFor="custom-query">Or enter custom query:</label>
          <div className="input-group">
            <input
              id="custom-query"
              type="text"
              value={selectedQuestion}
              onChange={(e) => setSelectedQuestion(e.target.value)}
              placeholder="Enter your query..."
              disabled={loading.vector || loading.topicEntity || loading.combined || loading.llmFiltered}
            />
            <button type="submit"               disabled={loading.vector || loading.topicEntity || loading.combined || loading.llmFiltered || !selectedQuestion.trim()}>
              {(loading.vector || loading.topicEntity || loading.combined || loading.llmFiltered) ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {((enabledMethods.vector && errors.vector) || 
        (enabledMethods.topicEntity && errors.topicEntity) ||
        (enabledMethods.combined && errors.combined) ||
        (enabledMethods.llmFiltered && errors.llmFiltered)) && (
        <div className="error-messages">
          {enabledMethods.vector && errors.vector && (
            <div className="error-message">
              <strong>Vector Error:</strong> {errors.vector}
            </div>
          )}
          {enabledMethods.topicEntity && errors.topicEntity && (
            <div className="error-message">
              <strong>Topic/Entity Error:</strong> {errors.topicEntity}
            </div>
          )}
          {enabledMethods.combined && errors.combined && (
            <div className="error-message">
              <strong>Combined Error:</strong> {errors.combined}
            </div>
          )}
          {enabledMethods.llmFiltered && errors.llmFiltered && (
            <div className="error-message">
              <strong>LLM Filtered Error:</strong> {errors.llmFiltered}
            </div>
          )}
        </div>
      )}

      {((enabledMethods.vector && (results.vector || loading.vector)) ||
        (enabledMethods.topicEntity && (results.topicEntity || loading.topicEntity)) ||
        (enabledMethods.combined && (results.combined || loading.combined)) ||
        (enabledMethods.llmFiltered && (results.llmFiltered || loading.llmFiltered))) && (
        <div className="search-results">
          <div className="results-header">
            <h3>Query: "{selectedQuestion}"</h3>
            {totalQuestions !== null && (
              <p className="db-stats">Total questions in DB: {totalQuestions}</p>
            )}
            {enabledMethods.topicEntity && (loading.topicEntity || queryAnalysis.topics.length > 0 || queryAnalysis.entities.length > 0) && (
              <div className="query-analysis">
                {loading.topicEntity ? (
                  <div className="analysis-loading">Extracting topics and entities from query...</div>
                ) : (
                  <>
                    <div className="analysis-section">
                      <strong>Extracted Topics:</strong>
                      {queryAnalysis.topics.length > 0 ? (
                        <div className="analysis-tags">
                          {queryAnalysis.topics.map((topic, idx) => (
                            <span key={idx} className="analysis-tag topic-tag">{topic}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="no-analysis">None</span>
                      )}
                    </div>
                    <div className="analysis-section">
                      <strong>Extracted Entities:</strong>
                      {queryAnalysis.entities.length > 0 ? (
                        <div className="analysis-tags">
                          {queryAnalysis.entities.map((entity, idx) => (
                            <span key={idx} className="analysis-tag entity-tag">
                              {entity.type}: {entity.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="no-analysis">None</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="results-grid">
            {/* Vector Results */}
            {enabledMethods.vector && (
            <div className="result-section">
              <h4>
                Vector Results
                {loading.vector && <span className="loading-badge">Loading...</span>}
                {!loading.vector && results.vector && (
                  <span className="result-count">({results.vector.length})</span>
                )}
                {timings.vector && (
                  <span className="timing-badge">{timings.vector}s</span>
                )}
              </h4>
              <div className="result-list">
                {loading.vector ? (
                  <div className="loading-in-section">Loading vector results...</div>
                ) : results.vector && results.vector.length > 0 ? (
                  results.vector.map((result, index) => (
                    <div key={result.id || index} className="result-item">
                      <div className="result-header">
                        <span className="result-index">#{index + 1}</span>
                        <span className="result-score">Score: {result.score?.toFixed(3) || 'N/A'}</span>
                      </div>
                      <div className="result-question">{result.questionText}</div>
                      {result.topics && result.topics.length > 0 && (
                        <div className="result-meta">
                          <strong>Topics:</strong> {result.topics.join(', ')}
                        </div>
                      )}
                      {result.video_link && (
                        <div className="result-link">
                          <a href={result.video_link} target="_blank" rel="noopener noreferrer">
                            Watch Video
                          </a>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="no-results">
                    No vector results (embeddings may not be available yet)
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Topic/Entity Results */}
            {enabledMethods.topicEntity && (
            <div className="result-section">
              <h4>
                Topic/Entity Results
                {loading.topicEntity && <span className="loading-badge">Loading...</span>}
                {!loading.topicEntity && results.topicEntity && (
                  <span className="result-count">({results.topicEntity.length})</span>
                )}
                {timings.topicEntity && (
                  <span className="timing-badge">{timings.topicEntity}s</span>
                )}
              </h4>
              <div className="result-list">
                {loading.topicEntity ? (
                  <div className="loading-in-section">Loading topic/entity results...</div>
                ) : results.topicEntity && results.topicEntity.length > 0 ? (
                  results.topicEntity.map((result, index) => (
                    <div key={result.id || index} className="result-item">
                      <div className="result-header">
                        <span className="result-index">#{index + 1}</span>
                        <span className="result-score">Score: {result.score?.toFixed(1) || 'N/A'}</span>
                      </div>
                      <div className="result-question">{result.questionText}</div>
                      {result.topics && result.topics.length > 0 && (
                        <div className="result-meta">
                          <strong>Topics:</strong> {result.topics.join(', ')}
                        </div>
                      )}
                      {result.entities && result.entities.length > 0 && (
                        <div className="result-meta">
                          <strong>Entities:</strong>{' '}
                          {result.entities.map(e => `${e.type}: ${e.name}`).join(', ')}
                        </div>
                      )}
                      {result.video_link && (
                        <div className="result-link">
                          <a href={result.video_link} target="_blank" rel="noopener noreferrer">
                            Watch Video
                          </a>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="no-results">No topic/entity matches found</div>
                )}
              </div>
            </div>
            )}

            {/* Combined Results */}
            {enabledMethods.combined && (
            <div className="result-section combined-section">
              <h4>
                Combined Results (Vector + Topics)
                {loading.combined && <span className="loading-badge">Loading...</span>}
                {!loading.combined && results.combined && (
                  <span className="result-count">({results.combined.length})</span>
                )}
                {timings.combined && (
                  <span className="timing-badge">{timings.combined}s</span>
                )}
              </h4>
              <div className="result-list">
                {loading.combined ? (
                  <div className="loading-in-section">Loading combined results...</div>
                ) : results.combined && results.combined.length > 0 ? (
                  results.combined.map((result, index) => (
                    <div key={result.id || index} className="result-item">
                      <div className="result-header">
                        <span className="result-index">#{index + 1}</span>
                        <div className="result-scores">
                          <span className="result-score">Combined: {result.score?.toFixed(3) || 'N/A'}</span>
                          {(result.vector_score !== null && result.vector_score !== undefined) ? (
                            <span className="result-score-detail">Vector: {result.vector_score.toFixed(3)}</span>
                          ) : (
                            <span className="result-score-detail" style={{color: '#ccc', fontStyle: 'italic'}}>Vector: N/A</span>
                          )}
                          {(result.topic_entity_score !== null && result.topic_entity_score !== undefined) ? (
                            <span className="result-score-detail">Topics: {result.topic_entity_score.toFixed(1)}</span>
                          ) : (
                            <span className="result-score-detail" style={{color: '#ccc', fontStyle: 'italic'}}>Topics: N/A</span>
                          )}
                        </div>
                      </div>
                      <div className="result-question">{result.questionText}</div>
                      {result.topics && result.topics.length > 0 && (
                        <div className="result-meta">
                          <strong>Topics:</strong> {result.topics.join(', ')}
                        </div>
                      )}
                      {result.video_link && (
                        <div className="result-link">
                          <a href={result.video_link} target="_blank" rel="noopener noreferrer">
                            Watch Video
                          </a>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="no-results">
                    No combined results
                  </div>
                )}
              </div>
            </div>
            )}

            {/* LLM Filtered Results */}
            {enabledMethods.llmFiltered && (
            <div className="result-section llm-filtered-section">
              <h4>
                LLM Filtered Results (Homepage)
                {loading.llmFiltered && <span className="loading-badge">Loading...</span>}
                {!loading.llmFiltered && results.llmFiltered && (
                  <span className="result-count">({results.llmFiltered.length})</span>
                )}
                {timings.llmFiltered && (
                  <span className="timing-badge">{timings.llmFiltered}s</span>
                )}
              </h4>
              <div className="result-list">
                {loading.llmFiltered ? (
                  <div className="loading-in-section">Loading LLM filtered results...</div>
                ) : results.llmFiltered && results.llmFiltered.length > 0 ? (
                  results.llmFiltered.map((result, index) => (
                    <div key={result.id || index} className="result-item">
                      <div className="result-header">
                        <span className="result-index">#{index + 1}</span>
                        <span className="result-score">LLM Selected</span>
                      </div>
                      <div className="result-question">{result.questionText}</div>
                      {result.topics && result.topics.length > 0 && (
                        <div className="result-meta">
                          <strong>Topics:</strong> {result.topics.join(', ')}
                        </div>
                      )}
                      {result.video_link && (
                        <div className="result-link">
                          <a href={result.video_link} target="_blank" rel="noopener noreferrer">
                            Watch Video
                          </a>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="no-results">
                    No LLM filtered results
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchDebug;
